import { z } from "zod";

const runIdField = {
  task_id: z.string().min(1),
  run_id: z.string().min(1),
};

export const usageSchema = z
  .object({
    input_tokens: z.number().int().nonnegative(),
    cached_input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    reasoning_output_tokens: z.number().int().nonnegative(),
  })
  .passthrough();

export const itemSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1),
  })
  .passthrough();

const itemEventTypeSchema = z.custom<`item.${string}`>((value) => typeof value === "string" && /^item\./.test(value));

const desktopEventTypeSchema = z.custom<`desktop.${string}`>(
  (value) => typeof value === "string" && /^desktop\./.test(value),
);

type LooseRecord = Record<string, unknown>;

export type AgentUsage = z.infer<typeof usageSchema>;
export type AgentItem = z.infer<typeof itemSchema>;

export type AgentUpdateBase = {
  task_id: string;
  run_id: string;
} & LooseRecord;

export type AgentItemUpdateEvent = {
  type: `item.${string}`;
  item: AgentItem;
} & AgentUpdateBase;

export type AgentUpdateEvent =
  | ({ type: "thread.started"; thread_id: string } & AgentUpdateBase)
  | ({ type: "turn.started" } & AgentUpdateBase)
  | ({ type: "turn.completed"; usage: AgentUsage } & AgentUpdateBase)
  | ({ type: "turn.failed" } & AgentUpdateBase)
  | ({ type: "error" } & AgentUpdateBase)
  | ({ type: "tui.started"; prompt: string } & AgentUpdateBase)
  | ({ type: "tui.completed" } & AgentUpdateBase)
  | ({ type: "tui.failed" } & AgentUpdateBase)
  | ({ type: `desktop.${string}` } & AgentUpdateBase)
  | AgentItemUpdateEvent;

export function isAgentItemUpdateEvent(event: AgentUpdateEvent): event is AgentItemUpdateEvent {
  return event.type.startsWith("item.");
}

export const agentUpdateEventSchema = z.union([
  z
    .object({
      ...runIdField,
      type: z.literal("thread.started"),
      thread_id: z.string().min(1),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("turn.started"),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("turn.completed"),
      usage: usageSchema,
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("turn.failed"),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("error"),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("tui.started"),
      prompt: z.string().trim().min(1),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("tui.completed"),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: z.literal("tui.failed"),
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: itemEventTypeSchema,
      item: itemSchema,
    })
    .passthrough(),
  z
    .object({
      ...runIdField,
      type: desktopEventTypeSchema,
    })
    .passthrough(),
]) as z.ZodType<AgentUpdateEvent>;

export type AgentUpdateRecord = {
  id: string;
  sequence: number;
  receivedAt: string;
  event: AgentUpdateEvent;
};

export type AgentUpdateSummary = {
  total: number;
  counts: Record<string, number>;
  currentTaskId: string | null;
  currentThreadId: string | null;
  latestUsage: AgentUsage | null;
  latestError: AgentUpdateRecord | null;
  latestFailure: AgentUpdateRecord | null;
};

export type AgentUpdatesSnapshot = {
  events: AgentUpdateRecord[];
  promptCommands: AgentPromptCommandRecord[];
  summary: AgentUpdateSummary;
};

export const runStatusSchema = z.enum(["running", "completed", "failed"]);
export const promptCommandStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
]);

export const createAgentTaskSchema = z
  .object({
    task_id: z.string().min(1),
    title: z.string().trim().min(1),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const createAgentRunSchema = z
  .object({
    run_id: z.string().min(1),
  })
  .strict();

export const createAgentPromptCommandSchema = z
  .object({
    task_id: z.string().min(1),
    prompt: z.string().trim().min(1),
  })
  .strict();

export const claimAgentPromptCommandSchema = z
  .object({
    run_id: z.string().min(1),
  })
  .strict();

export type AgentRunStatus = z.infer<typeof runStatusSchema>;
export type AgentPromptCommandStatus = z.infer<typeof promptCommandStatusSchema>;

export type AgentRunRecord = {
  id: string;
  taskId: string;
  status: AgentRunStatus;
  createdAt: string;
  updatedAt: string;
};

export type AgentTaskRecord = {
  id: string;
  title: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  runs: AgentRunRecord[];
};

export type AgentTasksSnapshot = {
  tasks: AgentTaskRecord[];
};

export type AgentPromptCommandRecord = {
  id: string;
  taskId: string;
  runId: string | null;
  prompt: string;
  status: AgentPromptCommandStatus;
  createdAt: string;
  updatedAt: string;
  claimedAt: string | null;
  heartbeatAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
};

export type AgentPromptCommandIssue = {
  id: string;
  taskId: string;
  prompt: string;
  createdAt: string;
};

export type AgentPromptCommandsSnapshot = {
  commands: AgentPromptCommandRecord[];
};

export type CreateAgentTaskRequest = z.infer<typeof createAgentTaskSchema>;
export type CreateAgentRunRequest = z.infer<typeof createAgentRunSchema>;
export type CreateAgentPromptCommandRequest = z.infer<typeof createAgentPromptCommandSchema>;
export type ClaimAgentPromptCommandRequest = z.infer<typeof claimAgentPromptCommandSchema>;
